import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    console.error('UI rendering error caught by AppErrorBoundary:', error);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[360px] items-center justify-center p-6">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-soft">
            <div className="mb-4 flex items-center gap-2 text-foreground">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <h2 className="text-base font-semibold">Something went wrong</h2>
            </div>
            <p className="mb-5 text-sm text-muted-foreground">
              Part of this page failed to load. You can retry rendering or refresh the application.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={this.handleReset}>Try Again</Button>
              <Button size="sm" variant="outline" onClick={this.handleReload}>Reload App</Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
